import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UploadDropzone } from './UploadDropzone';
import { ACCEPT_ATTR, validateFiles } from './validateFiles';
import type { KBDocument } from '@octery/shared';

const WARNING_HINT =
  'Embeddings could not be created, so search works by text only. Check the embeddings key or reindex the document.';

function IndexWarningBadge({ detail }: { detail?: string | null }) {
  const hint = detail ? `${WARNING_HINT} (${detail})` : WARNING_HINT;
  return (
    <span
      tabIndex={0}
      title={hint}
      aria-label={`Indexed without semantic search. ${hint}`}
      data-testid="index-warning"
      className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
    >
      <span aria-hidden="true">!</span> Indexed without semantic search
    </span>
  );
}

export function KnowledgeBasePage() {
  const { data: documents, isLoading, refetch } = useQuery({
    queryKey: ['knowledge-base', 'documents'],
    queryFn: () => api.get<KBDocument[]>('/api/knowledge-base/documents'),
  });

  const [uploading, setUploading] = useState(false);
  const pickerRef = useRef<HTMLInputElement>(null);

  const uploadFiles = async (files: File[]) => {
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await api.post('/api/knowledge-base/documents', formData);
        ok += 1;
      } catch (err) {
        Sentry.captureException(err, { extra: { filename: file.name } });
        const reason = err instanceof Error && err.message ? err.message : 'Upload failed';
        toast.error(`"${file.name}": ${reason}`);
      }
    }
    setUploading(false);
    if (ok) {
      refetch();
      toast.success(ok === 1 ? `"${files[0].name}" uploaded successfully` : `${ok} documents uploaded`);
    }
  };

  const handlePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { valid, rejected } = validateFiles(Array.from(e.target.files ?? []));
    rejected.forEach((r) => toast.error(`"${r.name}": ${r.reason}`));
    e.target.value = '';
    if (valid.length) void uploadFiles(valid);
  };

  const handleDelete = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await api.delete(`/api/knowledge-base/documents/${docId}`);
      refetch();
      toast.success('Document deleted');
    } catch (err) {
      Sentry.captureException(err, { extra: { docId } });
      toast.error('Failed to delete document');
    }
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
      processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
      indexed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
      error: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Knowledge Base</h1>
          <p className="text-muted-foreground">
            Upload documents for your AI agent to reference
          </p>
        </div>
        <div>
          <input
            ref={pickerRef}
            type="file"
            multiple
            hidden
            accept={ACCEPT_ATTR}
            onChange={handlePicked}
          />
          <Button onClick={() => pickerRef.current?.click()} disabled={uploading}>
            Upload Document
          </Button>
        </div>
      </div>

      <UploadDropzone onFiles={uploadFiles} uploading={uploading} />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-5 bg-muted rounded w-1/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !documents?.length ? (
        <p className="text-center text-muted-foreground">No documents uploaded yet</p>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <Card key={doc.id}>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="flex min-w-0 items-center gap-4">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{doc.filename}</p>
                    <p className="text-sm text-muted-foreground">
                      {(doc.sizeBytes / 1024).toFixed(1)} KB · {doc.chunksCount} chunks
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {doc.indexWarning === 'embeddings_failed' && <IndexWarningBadge detail={doc.indexWarningDetail} />}
                  {statusBadge(doc.status)}
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(doc.id)}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
